/**
 * A aba Dashboard: as corretivas da casa, só leitura, com o andamento.
 *
 * Papel: reproduz o painel `#panel-dashboard` do SM (l. 515–566) e o filtro de
 * `renderDashLike` (l. 915–932): busca em área, descrição e responsável — o
 * placeholder não diz, mas a origem procura no responsável também —,
 * multiseleções Área, Andamento e Responsável, e período sobre a atualização. A
 * corretiva entra quando passa nos cinco.
 *
 * Como usar: a página passa as corretivas já recortadas pela casa, sem filtro
 * nenhum, porque as opções das multiseleções saem delas. Todo filtro vive aqui e
 * sobrevive à troca de aba, porque o `QuadroEmbutido` mantém a aba montada. As
 * preventivas não aparecem nesta aba.
 *
 * "Exportar PDF" imprime a tabela como está na tela — com a busca, as três
 * multiseleções, o período e a ordenação em vigor — e nada mais, em A4 retrato
 * (`folha-a4-retrato`), com o `CabecalhoDeImpressao` no alto. O ritual é o
 * `useImpressao` da base com a marca `imprimindo-dashboard-de-manutencoes`; as
 * classes de impressão daqui, do kit, do rodapé e da página escrevem a mesma
 * marca por extenso, e uma marca trocada num lugar só deixa aquele elemento
 * aparecer no papel.
 */
import { type Corretiva } from '@concierge/shared';
import { IconFileDownload } from '@tabler/icons-react';
import { useState } from 'react';

import {
  FiltroDePeriodo,
  MultiSelecao,
  type OpcaoDeSelecao,
  dentroDoPeriodo,
  useImpressao,
  useMultiSelecao,
} from '@/shared/embutido';
import {
  opcoesDeArea,
  opcoesDeResponsavel,
  passaNaBusca,
  valorDaArea,
  valorDoResponsavel,
} from '../regras';
import {
  ORDEM_DOS_STATUS_CORRETIVA,
  PERCENTUAL_DO_ANDAMENTO,
  ROTULOS_STATUS_CORRETIVA,
} from '../rotulos';
import { type SituacaoDaLista } from '../types';
import { CabecalhoDeImpressao } from './cabecalho-de-impressao';
import { BarraDeFiltros, Espacador } from './kit/barra-de-filtros';
import { BotaoDaBarra } from './kit/botao-da-barra';
import {
  BuscaDaBarra,
  CLASSE_DA_DATA_DA_BARRA,
  CLASSE_DO_ROTULO_DA_BARRA,
} from './kit/campos-da-barra';
import { FaixaDoCartao } from './kit/faixa-do-cartao';
import { TabelaDashboard } from './tabela-dashboard';

/** As três opções fixas de andamento, com o percentual no rótulo (l. 531–533). */
const OPCOES_DE_ANDAMENTO: readonly OpcaoDeSelecao[] = ORDEM_DOS_STATUS_CORRETIVA.map(
  (status) => ({
    valor: status,
    rotulo: `${ROTULOS_STATUS_CORRETIVA[status]} (${String(PERCENTUAL_DO_ANDAMENTO[status])}%)`,
  }),
);

/** Os valores das opções de andamento, para o `useMultiSelecao`. */
const VALORES_DE_ANDAMENTO: readonly string[] = ORDEM_DOS_STATUS_CORRETIVA;

/** A marca que liga o CSS de impressão do Dashboard. */
const MARCA_DE_IMPRESSAO = 'imprimindo-dashboard-de-manutencoes';

/**
 * Uma opção cujo rótulo é o próprio valor.
 *
 * @param valor - A área ou o responsável.
 * @returns A opção da multiseleção.
 */
function opcao(valor: string): OpcaoDeSelecao {
  return { valor, rotulo: valor };
}

interface AbaDashboardProps {
  /** As corretivas da casa, sem filtro. */
  corretivas: readonly Corretiva[];
  situacao: SituacaoDaLista;
  /** Relê a lista depois de uma falha. */
  onTentarDeNovo: () => void;
}

/**
 * A aba Dashboard.
 *
 * @param corretivas - As corretivas da casa.
 * @param situacao - A situação da consulta.
 * @param onTentarDeNovo - Relê a lista.
 * @returns A faixa, a barra de filtros e a tabela.
 */
export function AbaDashboard({ corretivas, situacao, onTentarDeNovo }: AbaDashboardProps) {
  const [busca, setBusca] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const areas = opcoesDeArea(corretivas);
  const responsaveis = opcoesDeResponsavel(corretivas);
  const area = useMultiSelecao(areas);
  const andamento = useMultiSelecao(VALORES_DE_ANDAMENTO);
  const responsavel = useMultiSelecao(responsaveis);
  const imprimir = useImpressao(MARCA_DE_IMPRESSAO);
  const [geradoEm, setGeradoEm] = useState('');

  /** Escreve a data de hoje, em hora local, no cabeçalho do papel e imprime. */
  function handleExportarPdf() {
    setGeradoEm(new Date().toLocaleDateString('pt-BR'));
    void imprimir();
  }

  const filtradas = corretivas.filter(
    (corretiva) =>
      area.marcados.has(valorDaArea(corretiva)) &&
      andamento.marcados.has(corretiva.status) &&
      responsavel.marcados.has(valorDoResponsavel(corretiva)) &&
      dentroDoPeriodo(corretiva.atualizacao, de, ate) &&
      passaNaBusca(busca, [corretiva.area, corretiva.descricao, corretiva.responsavel]),
  );

  return (
    <div className="print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:w-[210mm] print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:bg-superficie print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:p-[5mm] print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:[page:folha-a4-retrato] print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:[print-color-adjust:exact] print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&_*]:[print-color-adjust:exact]">
      <CabecalhoDeImpressao geradoEm={geradoEm} />
      <div className="print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:hidden">
        <FaixaDoCartao>
          {'VILLA PRADO  ·  ACOMPANHAMENTO DE MANUTENÇÕES CORRETIVAS'}
        </FaixaDoCartao>
        <BarraDeFiltros>
          <BuscaDaBarra
            placeholder="Buscar por área ou item..."
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
          />
          <MultiSelecao
            prefixo="Área"
            opcoes={areas.map(opcao)}
            marcados={area.marcados}
            onMudar={area.definir}
          />
          <MultiSelecao
            prefixo="Andamento"
            opcoes={OPCOES_DE_ANDAMENTO}
            marcados={andamento.marcados}
            onMudar={andamento.definir}
          />
          <MultiSelecao
            prefixo="Responsável"
            opcoes={responsaveis.map(opcao)}
            marcados={responsavel.marcados}
            onMudar={responsavel.definir}
          />
          <FiltroDePeriodo
            rotulo="Atualização"
            de={de}
            ate={ate}
            onMudarDe={setDe}
            onMudarAte={setAte}
            classeDoRotulo={CLASSE_DO_ROTULO_DA_BARRA}
            classeDoCampo={CLASSE_DA_DATA_DA_BARRA}
          />
          <Espacador />
          <BotaoDaBarra onClick={handleExportarPdf}>
            <IconFileDownload size={11} stroke={2} aria-hidden="true" /> Exportar PDF
          </BotaoDaBarra>
        </BarraDeFiltros>
      </div>
      <TabelaDashboard
        linhas={filtradas}
        situacao={situacao}
        onTentarDeNovo={onTentarDeNovo}
      />
    </div>
  );
}
